import type { ThemeConfig } from 'antd';
import { theme } from 'antd';
import { CORAL, TEAL, SURFACE_1, SURFACE_1_SOFT } from './creamyTokens';

/**
 * The app's theme, in one place.
 *
 * Everything here used to live as a literal object in index.tsx plus about
 * forty `<ConfigProvider theme={{ token: { colorPrimary: ... } }}>` wrappers
 * scattered across screens. That arrangement had one root cause: the global
 * primary was BLACK, so every screen that wanted to look like the rest of
 * the app had to opt out of it by hand -- and the ones nobody remembered
 * (the header, password recovery, 2FA, six admin sections) simply stayed
 * black.
 *
 * Worse, black forced two global component overrides -- Menu and Select
 * selected-item text forced to white, because nothing else read against a
 * black fill -- which then had to be UNDONE inside every screen that
 * overrode the primary, since a coral fill is a light tint and white text
 * disappears on it. One bad token, a global patch, and a per-screen
 * un-patch.
 *
 * So the global primary is now the brand colour, both patches are gone, and
 * a screen that wants a different accent asks for it by name below.
 */

/**
 * Every form field in the app, rounded to the creamy pill radius (the same
 * 16px FloatingLabelInput's fields use, see index.css).
 *
 * This was a theme eleven screens opted into by hand, which is why the other
 * sixty had antd's default 6px rectangles -- including, until recently, the
 * 1.600-line publish wizard, whose buttons were pills and whose fields were
 * not. A rounded field is the app's field; a screen that wants a square one
 * can still say so.
 *
 * Still exported on its own so a subtree that sets its own accent can
 * compose the two rather than choosing between them.
 */
export const pillFormTheme: ThemeConfig = {
  components: {
    Input: { borderRadius: 16 },
    InputNumber: { borderRadius: 16 },
    Select: { borderRadius: 16 },
    // TimePicker shares DatePicker's namespace in antd v5, so this rounds
    // both. It used to be added by hand on the two availability screens; a
    // date is a form field like any other and belongs in the same rule.
    DatePicker: { borderRadius: 16 },
  },
};

/**
 * Tables, which in this app means the admin panel: 18 of its 22 sections
 * render one.
 *
 * antd's defaults are a spreadsheet -- a grey #fafafa header band, a hairline
 * between every header cell, square corners, tight rows. That is the single
 * most "generic tech product" surface left on the desktop, and it is the one
 * screen the platform's own operators live in all day.
 *
 * So: a warm header instead of a grey one, no vertical rules (the column
 * titles are enough), rounded top corners so the table reads as a card, and
 * a little more air in the rows.
 */
export const tableTheme: ThemeConfig = {
  components: {
    Table: {
      headerBg: SURFACE_1,
      headerColor: 'rgba(0, 0, 0, 0.65)',
      // antd draws a vertical hairline between header cells. Removing it is
      // most of the difference between "a table" and "a spreadsheet".
      headerSplitColor: 'transparent',
      headerBorderRadius: 14,
      rowHoverBg: SURFACE_1_SOFT,
      cellPaddingBlock: 14,
    },
  },
};

/** The whole application, unless a subtree says otherwise. */
export const appTheme: ThemeConfig = {
  algorithm: theme.defaultAlgorithm,
  token: {
    colorBgLayout: '#FFFFFF',
    // The brand colour, and the reason the two Menu/Select overrides that
    // used to sit here are gone: antd derives a light tint for selected
    // items from this, against which its own default dark text reads fine.
    colorPrimary: CORAL,
  },
  components: { ...pillFormTheme.components, ...tableTheme.components },
};

/**
 * Teal, for everything to do with booking and money moving.
 *
 * Deliberately NOT the brand coral: coral is "act on this", teal is "this is
 * the rental taking shape" -- the checkout summary, the availability
 * calendar, the hourly timeline, the albaran and the filters' sliders all
 * speak it. Wrap the subtree in a ConfigProvider with this rather than
 * writing `colorPrimary: TEAL` inline again.
 */
export const tealAccentTheme: ThemeConfig = {
  token: { colorPrimary: TEAL },
};

/**
 * The booking wizard's own flavour of the teal accent: the same colour plus
 * fully-rounded Radio groups, which is what its step controls are made of.
 */
export const bookingFlowTheme: ThemeConfig = {
  token: { colorPrimary: TEAL },
  components: { Radio: { borderRadius: 999 } },
};

/** The teal form: pill fields AND the teal accent, for the booking-side forms that want both. */
export const tealPillFormTheme: ThemeConfig = {
  token: tealAccentTheme.token,
  components: pillFormTheme.components,
};
